// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import type { DateValidationError } from '@mui/x-date-pickers';
import { PLANTING_STATUS, formatTime, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '../../lib/base';
import { DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import type {
  EditOrderTimeDataQuery,
  EditOrderTimeDataQueryVariables,
  EditOrderTimeMutation,
  EditOrderTimeMutationVariables,
} from '../../types/__generated__/graphql';
import {
  calculateAmountsNeededFromInputsAndExistingPlantings,
  createID,
  getTimeRanges,
  orderInputsToCalcInputs,
} from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { ExitDialogButton } from '../common/button';
import InternalDatePicker from '../common/date/InternalDatePicker';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import { type PlantingContainerData, PlantingListContainer } from './PlantingListContainer';

const EDIT_ORDER_TIME_MUT = gql`
  mutation EditOrderTime(
    $order_id: Int!
    $order_fulfillment_time: Int!
  ) {
    editOrderTime(
      input: {
        order_id: $order_id
        order_fulfillment_time: $order_fulfillment_time
      }
    ) {
      updated_order {
        id
        order_fulfillment_time
        updated_at
      }
      updated_plantings {
        id
        planting_state
        trays_rounded
        order_id
        updated_at
      }
      updated_tasks {
        id
        order_id
        updated_at
      }
      deleted_tasks {
        id
      }
      deleted_plantings {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;
const EDIT_ORDER_TIME_DATA = gql`
  query EditOrderTimeData($id: ID!) {
    order(id: $id) {
      id
      order_fulfillment_time
      inputs {
        edges {
          node {
            id
            size_amount
            quantity
            unit
            crop {
              id
              crop_name
              yield_per_tray
              yield_unit
              total_grow_time
              harvest_buffer_time
            }
            blend {
              id
              blend_name
              status
              crops {
                edges {
                  node {
                    id
                    percent_of_blend
                    crop {
                      id
                      crop_name
                      yield_per_tray
                      yield_unit
                      total_grow_time
                      harvest_buffer_time
                    }
                  }
                }
              }
            }
          }
        }
      }
      plantings {
        edges {
          node {
            id
            planting_status
            trays_rounded
            yield_per_tray_ounces
            crop {
              id
              crop_name
              yield_per_tray
              yield_unit
              total_grow_time
              harvest_buffer_time
              steps {
                edges {
                  node {
                    id
                    name
                    time_to_start_after_first_step
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly orderId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

export default function EditOrderTimeDialog({ orderId, isOpen, onClose, onSuccess }: Props) {
  const { timezone } = useUserContext();
  const apolloClient = useApolloClient();
  const [editOrderTime, { loading: editSingleOrderLoading }] = useMutation<
    EditOrderTimeMutation,
    EditOrderTimeMutationVariables
  >(EDIT_ORDER_TIME_MUT);
  const [getOrderData, { data: orderData, loading: orderDataLoading, error: orderDataError }] = useLazyQuery<
    EditOrderTimeDataQuery,
    EditOrderTimeDataQueryVariables
  >(EDIT_ORDER_TIME_DATA, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      void getOrderData({
        variables: { id: createID.order(orderId) },
        onCompleted: (orderDataIn) => {
          if (orderDataIn?.order != null) {
            if (orderDataIn.order.order_fulfillment_time != null) {
              const time = dayjs.unix(orderDataIn.order.order_fulfillment_time).tz(timezone);
              setExistingFulfillmentTime(time);
              setFulfillmentTime(time);
            }
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, orderId, getOrderData, timezone]);

  const disableFields = editSingleOrderLoading || orderDataLoading;

  // This is to prevent a flicker of the old date when the dialog is opened the second time
  const onCloseInternal = () => {
    setFulfillmentTime(existingFulfillmentTime);
    onClose();
  };

  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);
  const [existingFulfillmentTime, setExistingFulfillmentTime] = React.useState<dayjs.Dayjs | null>(null);
  const [fulfillmentTime, setFulfillmentTime] = React.useState<dayjs.Dayjs | null>(null);
  const [fulfillmentTimeError, setFulfillmentTimeError] = React.useState<DateValidationError | null>(null);
  const fulfillmentTimeErrorMessage = React.useMemo(() => {
    if (fulfillmentTimeError == null) {
      return '';
    }
    switch (fulfillmentTimeError) {
      case 'minDate':
        return 'Delivery date cannot be before today';
      default:
        return '';
    }
  }, [fulfillmentTimeError]);

  const onChangeFullfillmentTime = (newValue: dayjs.Dayjs | null) => {
    setFulfillmentTime(newValue);
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    dayjs.extend(utc);

    void editOrderTime({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
        {
          query: SURPLUSES_BUFFERS_QUERY,
          variables: {
            timeAfter: todayStart,
          },
        },
      ],
      update: (cache, { data }) => {
        const plantingsCreated = (data?.editOrderTime?.plantings_created?.length ?? 0) > 0;
        const plantingsDeleted = (data?.editOrderTime?.deleted_plantings?.length ?? 0) > 0;
        const tasksCreated = (data?.editOrderTime?.tasks_created?.length ?? 0) > 0;
        const tasksDeleted = (data?.editOrderTime?.deleted_tasks?.length ?? 0) > 0;
        cache.modify({
          fields: {
            harvests(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantings(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingGroup;
            },
            tasks(existingTasks, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingTasks;
            },
            taskGroup(existingGroup, { DELETE }) {
              return tasksCreated ? DELETE : existingGroup;
            },
            plantingGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            planting_groups(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantingTimeGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
          },
        });
      },
      variables: {
        order_id: orderId,
        order_fulfillment_time: fulfillmentTime == null ? 0 : utils.toNoonTime(fulfillmentTime, timezone).unix(),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editOrderTime != null) {
          const updatedOrderData = dataIn.editOrderTime;
          enqueueSnackbar('Updated order time', { variant: 'success' });
          const newTime = dayjs.unix(updatedOrderData.updated_order.order_fulfillment_time).tz(timezone);
          setFulfillmentTime(newTime);
          setExistingFulfillmentTime(newTime);
          setFulfillmentTimeError(null);
          evictCacheData({
            apolloClient,
            taskIds: updatedOrderData.deleted_tasks.map((t) => t.id),
            plantingIds: updatedOrderData.deleted_plantings.map((p) => p.id),
          });
          onSuccess(true);
        } else {
          enqueueSnackbar('Error editing order', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const hasInputValidationError = fulfillmentTimeErrorMessage !== '';
  const requiredFieldsFilled: boolean = fulfillmentTime !== null;
  const timeChanged = existingFulfillmentTime?.unix() !== fulfillmentTime?.unix();
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields || !timeChanged;
  const onEnter = () => {
    if (!isSubmitDisabled && !disableFields) {
      onSubmit();
    }
  };

  const plantings = orderData?.order?.plantings.edges.map((edge) => edge.node) ?? [];
  const plannedPlantings = plantings.filter((planting) => planting.planting_status === PLANTING_STATUS.PLANNED);
  const inProgressPlantings = plantings.filter((planting) => planting.planting_status === PLANTING_STATUS.IN_PROGRESS);
  const completedPlantings = plantings.filter((planting) => planting.planting_status === PLANTING_STATUS.COMPLETED);

  const amountsNeeded = calculateAmountsNeededFromInputsAndExistingPlantings(
    orderInputsToCalcInputs(orderData?.order?.inputs?.edges?.map((edge) => edge.node) ?? []),
    plantings,
  );

  // Calculate the new plantings, but with extra
  const plantingsToCreate: (PlantingContainerData & { readonly isTooLateToStart: boolean })[] = [
    ...amountsNeeded.values(),
  ].map(({ traysNeeded: traysNeededInternal, cropData: crop }) => {
    const growTime = crop.totalGrowTime;
    const noonOnOrderDate = fulfillmentTime == null ? 0 : utils.toNoonTime(fulfillmentTime, timezone).unix();
    const harvestTime = noonOnOrderDate - (crop.harvestBufferTime ?? 0);
    const startTime = harvestTime - growTime;
    const tooLateToPlant = startTime < todayStart;

    const yieldPerTrayOunces = utils.convert({
      inputUnit: crop.yieldUnit,
      outputUnit: 'ounces',
      quantity: crop.yieldPerTray,
    });

    return {
      id: crop.id.toString(),
      crop: {
        id: crop.id,
        crop_name: crop.cropName,
        yield_unit: crop.yieldUnit,
      },
      isTooLateToStart: tooLateToPlant,
      yield_per_tray_ounces: yieldPerTrayOunces.toString(),
      trays_rounded: traysNeededInternal.toString(),
      extra: `Start Date: ${dayjs.unix(startTime).format('MM/DD/YYYY')}${
        tooLateToPlant ? ' (Too Late)' : ''
      }, Harvest Date: ${dayjs.unix(harvestTime).format('MM/DD/YYYY')}`,
    };
  });

  const plantingsToCreateNotTooLate = plantingsToCreate.filter((planting) => !planting.isTooLateToStart);
  const plantingsToCreateTooLate = plantingsToCreate.filter((planting) => planting.isTooLateToStart);

  const dialogContent = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <InternalDatePicker
        label={'New Delivery Date *'}
        value={fulfillmentTime}
        onChange={onChangeFullfillmentTime}
        disabled={disableFields}
        minDate={utils.todayStart(timezone)}
        onError={(newError) => setFulfillmentTimeError(newError)}
        textFieldProps={{
          helperText: fulfillmentTimeErrorMessage,
        }}
      />
      {existingFulfillmentTime && (
        <Typography>{`Existing delivery date: ${formatTime(existingFulfillmentTime.unix(), { timezone })}`}</Typography>
      )}
      {timeChanged &&
        (orderDataLoading ? (
          <PageLoading />
        ) : orderDataError ? (
          <ErrorView error={orderDataError} />
        ) : (
          <Box>
            {plannedPlantings.length > 0 && (
              <PlantingListContainer
                plantingsIn={plannedPlantings}
                title="These planned plantings will be deleted:"
                showAmount={false}
              />
            )}
            {inProgressPlantings.length > 0 && (
              <PlantingListContainer
                plantingsIn={inProgressPlantings}
                title="These in progress plantings will be added to your surpluses:"
                showAmount={true}
              />
            )}
            {completedPlantings.length > 0 && (
              <PlantingListContainer
                plantingsIn={completedPlantings}
                title="These completed plantings will be removed from the order:"
                showAmount={true}
              />
            )}
            {plantingsToCreateNotTooLate.length > 0 && (
              <PlantingListContainer
                plantingsIn={plantingsToCreateNotTooLate}
                title="These plantings will be created:"
                showAmount={true}
              />
            )}
            {plantingsToCreateTooLate.length > 0 && (
              <PlantingListContainer
                plantingsIn={plantingsToCreateTooLate}
                title="These plantings will be created but note that they are too late to start:"
                showAmount={true}
              />
            )}
          </Box>
        ))}
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseInternal();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Order Delivery Date</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onCloseInternal} />
        </Box>
      </DialogTitle>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onEnter();
          }
        }}
        sx={{ padding: 1 }}
      >
        {dialogContent}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onCloseInternal}
          disabled={disableFields}
        >
          Cancel
        </Button>
        <Button
          id="submit-order-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter();
            }
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
